import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/lib/supabase";
import { NanaeLogoSymbol, NanaeWordmark } from "@/components/qv3/NanaeLogo";
import { socialMeta } from "@/lib/seo-meta";
import {
  clearPendingInvitation,
  invitationErrorMessage,
  readPendingInvitation,
  redeemInvitationCode,
} from "@/lib/invitation";

export const Route = createFileRoute("/auth/callback")({
  head: () => ({
    meta: [{ title: "Connexion en cours — Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Connexion en cours — Hōjin",
        description: "Connexion en cours — Hōjin",
        url: "https://hojin.ai/auth/callback",
        image: "conseil",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/auth/callback" }],
  }),
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const navigate = useNavigate();

  async function redeemPendingInvitation() {
    const code = readPendingInvitation();
    if (!code) return;
    try {
      await redeemInvitationCode(code);
    } catch (error) {
      // Le compte est déjà créé : un code refusé ne doit jamais empêcher
      // l'accès au Foyer. Le détail sera visible dans les paramètres.
      console.warn("[invitation]", invitationErrorMessage(error));
    } finally {
      clearPendingInvitation();
    }
  }

  useEffect(() => {
    let cancelled = false;

    // ⚠️ UN LIEN PÉRIMÉ N'ARRIVE PAS COMME UNE PANNE — IL ARRIVE AVEC SON MOTIF.
    //
    // Supabase renvoie l'échec d'un lien à usage unique dans l'URL : en query (flux PKCE) ou dans
    // le FRAGMENT (flux implicite, `#error=access_denied&error_code=otp_expired`). Le fragment ne
    // part jamais au serveur — le lire ici est le seul endroit possible.
    //
    // Sans cette lecture, un lien expiré tombait dans le fourre-tout et l'utilisateur lisait
    // « La connexion avec Google / Apple a échoué » : un message faux, sur un service qu'il n'a pas
    // utilisé, pour un événement parfaitement normal. Un lien à usage unique QUI A SERVI n'est pas
    // une erreur : c'est son fonctionnement.
    const motifDeLUrl = (): string | null => {
      if (typeof window === "undefined") return null;
      const q = new URLSearchParams(window.location.search);
      const h = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const code = q.get("error_code") ?? h.get("error_code");
      const err = q.get("error") ?? h.get("error");
      if (!code && !err) return null;
      if (code && /otp_expired|invalid|expired|used/i.test(code)) return "lien_expire";
      if (err === "access_denied") return "lien_expire";
      return "oauth_failed";
    };

    (async () => {
      try {
        const motif = motifDeLUrl();
        if (motif) {
          navigate({ to: "/login", search: { error: motif } as never, replace: true });
          return;
        }
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error || !data.session) {
          const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
            if (session) {
              sub.subscription.unsubscribe();
              void redeemPendingInvitation().finally(() => {
                if (!cancelled) navigate({ to: "/foyer", replace: true });
              });
            }
          });
          setTimeout(() => {
            sub.subscription.unsubscribe();
            // Aucune session au bout de huit secondes ET aucun motif dans l'URL : on ne sait pas
            // pourquoi. On renvoie sur le motif du LIEN plutôt que sur celui d'OAuth — c'est le
            // chemin normal désormais, et proposer un nouveau lien est utile dans les deux cas.
            if (!cancelled) {
              navigate({ to: "/login", search: { error: "lien_expire" } as never, replace: true });
            }
          }, 8000);
          return;
        }
        await redeemPendingInvitation();
        navigate({ to: "/foyer", replace: true });
      } catch {
        if (!cancelled) {
          navigate({ to: "/login", search: { error: "lien_expire" } as never, replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Vérification de votre identité"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "#000000",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 24,
        zIndex: 30,
      }}
    >
      <div
        style={{
          animation: "nanaeAuthPulse 1.4s ease-in-out infinite",
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        <NanaeLogoSymbol size="lg" />
        <NanaeWordmark size="lg" as="h1" />
      </div>
      <p
        style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontStyle: "italic",
          fontSize: 16,
          color: "#FF1744",
          letterSpacing: "0.5px",
          margin: 0,
        }}
      >
        Le Conseil vérifie votre identité…
      </p>
      <style>{`
        @keyframes nanaeAuthPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
    </div>
  );
}
